import React, { useCallback, useContext, useEffect, useReducer } from "react";

import { UserInfoResponse, getUserInformation } from "../../../api/user/info";
import { OrganizationInfoResponse, getOrganizationInfo } from "../../../api/organization/get";
import { getOrganizationMembers } from "../../../api/organization/member/get";
import { assertNever } from "../utils";
import { UserInfo } from "./UserInfo";
import { OrganizationDetails } from "./OrganizationDetails";
import { MemberManagement } from "./MemberManagement";
import { CurrentMember, OrganizationMember, PendingMember } from "./types";
import { UserContext } from "../../../context/UserContext";

import "../../../style/dashboard/settings.module.css";

type PageState =
    | {
          status: "SUCCESS";
          user: UserInfoResponse;
          org: OrganizationInfoResponse;
          orgMembers: OrganizationMember[];
      }
    | { status: "LOADING" | "ERROR" | "IDLE" };

const INITIAL_PAGE_STATE: PageState = {
    status: "IDLE"
};

type PageAction =
    | { type: "update_state"; payload: PageState }
    | { type: "delete_member"; payload: string }
    | { type: "update_member"; payload: CurrentMember }
    | { type: "add_org"; payload: OrganizationInfoResponse }
    | { type: "add_pending_members"; payload: PendingMember[] }
    | { type: "update_current_user"; payload: UserInfoResponse };

function pageReducer(state: PageState, action: PageAction): PageState {
    switch (action.type) {
        case "update_state": {
            return action.payload;
        }
        case "add_org": {
            if (state.status !== "SUCCESS") return state;
            return { ...state, org: action.payload };
        }
        case "add_pending_members": {
            if (state.status !== "SUCCESS") return state;
            return { ...state, orgMembers: [...state.orgMembers, ...action.payload] };
        }
        case "delete_member": {
            if (state.status !== "SUCCESS") return state;
            return {
                ...state,
                orgMembers: state.orgMembers.filter(orgMember => orgMember.email !== action.payload)
            };
        }
        case "update_member": {
            if (state.status !== "SUCCESS") return state;
            return {
                ...state,
                orgMembers: state.orgMembers.map(orgMember => {
                    return action.payload.email === orgMember.email ? action.payload : orgMember;
                })
            };
        }
        case "update_current_user":
            if (state.status !== "SUCCESS") return state;
            return {
                ...state,
                orgMembers: state.orgMembers.map(orgMember => {
                    return action.payload.email === orgMember.email
                        ? { ...orgMember, name: action.payload.name }
                        : orgMember;
                }),
                user: action.payload
            };

        default:
            assertNever(action);
            return state;
    }
}

export default function Settings() {
    const [pageState, updatePageState] = useReducer(pageReducer, INITIAL_PAGE_STATE);
    const userContext = useContext(UserContext);

    const pageInitApiCall = useCallback(async () => {
        updatePageState({ type: "update_state", payload: { status: "LOADING" } });
        try {
            const [user, org, orgMembersDetails] = await Promise.all([
                getUserInformation(),
                getOrganizationInfo(),
                getOrganizationMembers()
            ]);
            updatePageState({
                type: "update_state",
                payload: {
                    status: "SUCCESS",
                    user,
                    org,
                    orgMembers: orgMembersDetails.organizationFound
                        ? orgMembersDetails.members
                        : // default to the logged in user
                          [
                              {
                                  userId: user.userId,
                                  name: user.name,
                                  email: user.email,
                                  joinedDate: user.joinedDate,
                                  role: "owner"
                              }
                          ]
                }
            });
        } catch (e) {
            updatePageState({ type: "update_state", payload: { status: "ERROR" } });
        }
    }, []);

    useEffect(() => {
        pageInitApiCall();
    }, []);

    switch (pageState.status) {
        case "ERROR":
            return (
                <div className="saas-settings-container centered">
                    <div id="dashboard-error-text">Something went wrong</div>
                    <div onClick={pageInitApiCall} id="dashboard-error-button">
                        Try again
                    </div>
                </div>
            );
        case "IDLE":
        case "LOADING":
            return (
                <div className="saas-settings-container centered">
                    <img className="loader" src="/static/assets/dashboard/loading.svg" alt="loading" />
                </div>
            );
        case "SUCCESS":
            const { user, org, orgMembers } = pageState;
            return (
                <div className="saas-settings-container">
                    <OrganizationDetails org={org} user={user} />
                    <UserInfo
                        user={user}
                        onUserUpdate={user => {
                            updatePageState({
                                type: "update_current_user",
                                payload: user
                            });

                            if (userContext) {
                                userContext.updateUser(user);
                            }
                        }}
                        org={org}
                    />
                    <MemberManagement
                        orgMembers={orgMembers}
                        user={user}
                        onMemberDeleted={email => {
                            updatePageState({ type: "delete_member", payload: email });
                        }}
                        onOrgCreated={org => {
                            updatePageState({ type: "add_org", payload: org });
                        }}
                        onMembersInvited={pendingMembers => {
                            updatePageState({ type: "add_pending_members", payload: pendingMembers });
                        }}
                        org={org}
                        onMemberUpdated={currentMember => {
                            updatePageState({ type: "update_member", payload: currentMember });
                        }}
                    />
                </div>
            );
        default:
            assertNever(pageState);
            return null;
    }
}
